/**
 * The app's motion vocabulary.
 *
 * Written down because it had already been written five times. A survey of every `initial` in the
 * codebase found the same fade-and-rise expressed with `y` of 4, 6, 8, 9 and 10, and durations of
 * 0.2 through 0.5 chosen per call site — which is how two screens end up feeling like two
 * products. These are the values those uses were converging on.
 *
 * Tokens here, components in components/ui/motion.tsx. Anything that needs a shape not in this
 * file is either a new token — add it here — or genuinely bespoke choreography, which some of the
 * research canvas legitimately is.
 */

/**
 * The standard curve: fast out of the gate, settling rather than decelerating evenly.
 *
 * Taken from the example-question list in the canvas, which is the app's most-looked-at
 * animation. It is what makes a cascade read as a single movement rather than as N separate ones.
 */
export const EASE = [0.22, 1, 0.36, 1] as const

/** Leaving is always quicker than arriving — an exit is usually something the reader just did. */
export const DURATION = {
  /** Exits, and anything acknowledging a click. */
  quick: 0.18,
  /** Crossfades between two states of the same thing. */
  cross: 0.2,
  /** The default: something arriving. */
  base: 0.3,
  /** Larger surfaces, where the base duration reads as abrupt. */
  slow: 0.45,
} as const

/** How far a rising element travels. One value, so nothing drifts to 9 or 10 again. */
export const RISE_Y = 8

/** And how much it scales up from — small enough to be felt rather than seen. */
export const RISE_SCALE = 0.985

/**
 * Cascade timing.
 *
 * `CAP` is the part worth keeping: a fixed list of five can afford to stagger all the way down,
 * but an arbitrarily long one cannot, or its last row lands seconds after its first and the page
 * reads as broken. Past the cap every remaining item shares the last delay and they arrive
 * together, which looks intentional at any length.
 */
export const STAGGER = {
  step: 0.045,
  cap: 8,
  /** A beat before the first item, so the cascade is legible from its start. */
  lead: 0.04,
} as const

export function staggerDelay(index: number): number {
  return STAGGER.lead + Math.min(index, STAGGER.cap) * STAGGER.step
}

/**
 * How long to wait before letting new items into a list that is rearranging.
 *
 * Framer animates layout by rendering an element at its NEW position and transforming it to look
 * like it is still at the old one. So mid-rearrangement, a row's DOM slot and the place it
 * appears are different — and anything inserted into that slot fades in on top of a neighbour
 * that has not visually vacated it yet. Waiting for the move to finish is what stops arrivals
 * landing on the things making room for them.
 */
export const ENTER_AFTER_LAYOUT = DURATION.base

/** For something that should arrive with weight — a button appearing under the cursor. */
export const SPRING = {
  /** Buttons and small controls. */
  pop: { type: "spring", stiffness: 540, damping: 26 },
  /** Panels and rows of controls. */
  settle: { type: "spring", stiffness: 460, damping: 26 },
} as const
